import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'palette/Button';
import { PaletteConfigProvider } from 'palette/Config';
import { Icon } from 'palette/Icon';
import { Header } from 'palette/Typography';
import { getCardTranslations } from '@common/Favorites/helpers/cardTranslations';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { Product } from '@common/Listing/types';
import { MyListsCard } from '@common/MyLists/containers/MyListsCard';
import { openRecents } from '@common/MyLists/store/actions/MyListsActions';
import { ItemsParameters, ItemInteractionOrigin } from '@common/Product/AddToFavorites/types';
import { trackProductImpressions } from '@common/Tracking';
import { isBoozt } from '@helpers/env';
import { RecentsCounter } from '../containers/RecentsCounter';

interface RecentsProductsSimpleProps {
    products: Product[];
    maxProducts: number;
    itemsParameters: ItemsParameters;
    interactionOrigin: ItemInteractionOrigin;
}

export function RecentsProductsSimple({
    products = [],
    maxProducts = 6,
    itemsParameters,
    interactionOrigin,
}: RecentsProductsSimpleProps) {
    const dispatch = useDispatch();

    const config = useConfig();
    const translate = useTranslation();

    const productsContainerRef = useRef<HTMLUListElement | null>(null);
    const { URLPrefix, SVGURL } = config;
    const recentsUrl = `${URLPrefix}/customer/my-lists#list=recently`;

    const openRecentsClick = (event: React.MouseEvent) => {
        event.preventDefault();
        dispatch(openRecents());
    };

    useEffect(() => {
        if (productsContainerRef && isBoozt()) {
            trackProductImpressions(productsContainerRef.current);
        }
    }, [productsContainerRef]);

    return (
        <PaletteConfigProvider
            device="desktop"
            svgPath={SVGURL}
        >
            <a
                className="product-tabs__title"
                href={recentsUrl}
                onClick={openRecentsClick}
            >
                <Header
                    color="strong"
                    level="6"
                >
                    {translate('product.details.recently_viewed.label')}
                </Header>
            </a>
            <ul
                ref={productsContainerRef}
                className="product-tabs__list product-tabs__list--recently"
                data-current-page={1}
                data-products-group={itemsParameters.item_list_name}
                data-products-per-page={maxProducts}
                data-total-products={products.length}
            >
                {products.length > 0 ? (
                    products
                        .slice(0, maxProducts)
                        .map((product, index) => {
                            const bmpTrackingData = {
                                totalProducts: products.length,
                                productsPerPage: products.length,
                                itemIndex: index,
                                group: itemsParameters.item_list_name,
                                containerOffsetWidth: productsContainerRef.current?.offsetWidth,
                            };
                            return (
                                <MyListsCard
                                    key={`${product?.variants?.length > 0 ? product.variants[0].id : product?.id}
                                    __recentlyViewedPDP`}
                                    cardStrings={getCardTranslations(translate)}
                                    containerClassName="product-tabs__product"
                                    product={product}
                                    trackingValues={{
                                        list: itemsParameters.item_list_name,
                                        bmp: bmpTrackingData,
                                        itemsParameters,
                                        interactionOrigin,
                                    }}
                                    type="recentlyViewedPDP"
                                />
                            );
                        })
                ) : (
                    <li className="message-empty">
                        <div className="message-empty__thumb">
                            <Icon
                                name="palette_eye"
                            />
                        </div>
                        {translate('customer_my_lists_recently_empty')}
                    </li>
                )}
            </ul>
            {products.length > maxProducts
                && (
                    <Button
                        className="view-all"
                        size="small"
                        theme="secondary"
                        onClick={openRecentsClick}
                    >
                        {translate('helper.view_all.label')}
                        {' '}
                        (
                        <RecentsCounter />
                        )
                    </Button>
                )}
        </PaletteConfigProvider>
    );
}
